import { Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle, Mail, ClipboardList } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, Stagger, StaggerItem, SectionLabel } from "../components/Motion";
import { waLink, BUSINESS_EMAIL } from "../lib/site";

const CHANNELS = [
  {
    icon: MessageCircle,
    title: "WhatsApp",
    copy: "Fastest way to reach our team for sourcing discussions.",
    action: "Chat With HAQILA",
    href: waLink(),
    external: true,
    testId: "contact-whatsapp-card",
  },
  {
    icon: Mail,
    title: "Email",
    copy: BUSINESS_EMAIL,
    action: "Send an Email",
    href: `mailto:${BUSINESS_EMAIL}`,
    external: true,
    testId: "contact-email-card",
  },
  {
    icon: ClipboardList,
    title: "Inquiry Form",
    copy: "Structured RFQ with product, quantity, packaging and destination details.",
    action: "Request a Quote",
    href: "/request-a-quote",
    external: false,
    testId: "contact-form-card",
  },
];

export default function Contact() {
  return (
    <>
      <SEO />
      <div data-testid="contact-page" className="bg-offwhite pt-40 pb-24 sm:pb-32 min-h-screen">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
          <SectionLabel index="CONTACT" title="Get in Touch" />
          <Reveal className="mt-8 max-w-4xl">
            <h1 data-testid="contact-primary-heading" className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.98]">
              Let's Talk About Your Requirements
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-charcoal/65 leading-relaxed">
              Discuss Indonesian coconut charcoal sourcing, wholesale supply or export requirements with HAQILA.
              Our team can review your quotation, product specification, COA and sample requests.
            </p>
          </Reveal>

          <Stagger className="mt-16 grid md:grid-cols-3 gap-px bg-charcoal/10 border border-charcoal/10" gap={0.1}>
            {CHANNELS.map((channel) => (
              <StaggerItem key={channel.title}>
                <div data-testid={channel.testId} className="bg-offwhite hover:bg-sand p-8 sm:p-10 h-full flex flex-col gap-6 transition-colors duration-300">
                  <channel.icon className="w-7 h-7 text-forest" strokeWidth={1.5} />
                  <div>
                    <h2 className="font-display text-2xl font-bold tracking-tight">{channel.title}</h2>
                    <p className="mt-2 text-sm text-charcoal/60 leading-relaxed break-all">{channel.copy}</p>
                  </div>
                  {channel.external ? (
                    <a
                      href={channel.href}
                      target={channel.href.startsWith("http") ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      data-testid={`${channel.testId}-action`}
                      className="group mt-auto inline-flex items-center gap-2 text-sm font-semibold text-forest hover:text-clay transition-colors duration-300"
                    >
                      {channel.action}
                      <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  ) : (
                    <Link
                      to={channel.href}
                      data-testid={`${channel.testId}-action`}
                      className="group mt-auto inline-flex items-center gap-2 text-sm font-semibold text-forest hover:text-clay transition-colors duration-300"
                    >
                      {channel.action}
                      <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  )}
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal className="mt-20 bg-charcoal text-offwhite p-10 sm:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Send Your Inquiry</h2>
              <p className="mt-3 text-sm text-offwhite/60 max-w-lg leading-relaxed">
                Share your product, specification, quantity and destination port — we'll respond with sourcing options
                from Indonesia.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 shrink-0">
              <Link
                to="/request-a-quote"
                data-testid="contact-inquiry-cta"
                className="group inline-flex items-center gap-3 bg-forest hover:bg-moss text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300"
              >
                Send Your Inquiry
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-cta"
                className="inline-flex items-center gap-3 border border-offwhite/30 hover:border-offwhite text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300"
              >
                Chat With HAQILA
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </>
  );
}
